import { addDays, format, isSameDay } from 'date-fns';
import { studioDayKey, studioDayStartIso } from '@atink/domain';
import { defaultArtistDeposits } from '@/domain/pricing';
import type { Artist, ArtistAvailability, ArtistRates, DayOfWeek, LeaveBlock, StudioId } from './types';

const DEFAULT_RATES: ArtistRates = { hourlyAud: 180, smallAud: 250, halfDayAud: 600, fullDayAud: 1200 };

const WEEKDAY_WINDOW = { startTime: '10:00', endTime: '19:00' };

export function emptyAvailability(): ArtistAvailability {
  const pattern: Record<DayOfWeek, { startTime: string; endTime: string } | null> = {
    mon: { ...WEEKDAY_WINDOW },
    tue: { ...WEEKDAY_WINDOW },
    wed: { ...WEEKDAY_WINDOW },
    thu: { ...WEEKDAY_WINDOW },
    fri: { ...WEEKDAY_WINDOW },
    sat: null,
    sun: null,
  };
  return { pattern, rdoDates: [], leaveBlocks: [], perDayCapacity: 3 };
}

export function addLeaveBlock(av: ArtistAvailability, blockToAdd: LeaveBlock): ArtistAvailability {
  return { ...av, leaveBlocks: [...av.leaveBlocks, blockToAdd] };
}

export function removeLeaveBlock(av: ArtistAvailability, id: string): ArtistAvailability {
  return { ...av, leaveBlocks: av.leaveBlocks.filter((b) => b.id !== id) };
}

/** Remove just this dated exception, retaining both sides of longer leave. */
export function removeDayOffDate(av: ArtistAvailability, date: string): ArtistAvailability {
  const day = new Date(`${date}T00:00:00`);
  return {
    ...av,
    rdoDates: av.rdoDates.filter(d => d !== date),
    leaveBlocks: av.leaveBlocks.flatMap(block => {
      const start = studioDayKey(block.startDate);
      const end = studioDayKey(block.endDate);
      if (date < start || date > end) return [block];
      const left = start < date ? [{ ...block, endDate: studioDayStartIso(format(addDays(day, -1), 'yyyy-MM-dd')) }] : [];
      const right = date < end ? [{ ...block, id: left.length ? `${block.id}-after-${date}` : block.id, startDate: studioDayStartIso(format(addDays(day, 1), 'yyyy-MM-dd')) }] : [];
      return [...left, ...right];
    }),
  };
}

export function upcomingLeave(av: ArtistAvailability, nowIso: string): LeaveBlock[] {
  const cutoff = new Date(nowIso).getTime() - 24 * 60 * 60 * 1000;
  return av.leaveBlocks
    .filter((b) => new Date(b.endDate).getTime() >= cutoff)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
}

export function slugifyArtistId(displayName: string, existingIds: Set<string>): string {
  const base =
    displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'artist';
  if (!existingIds.has(base)) return base;
  let n = 2;
  while (existingIds.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

export function formatLeaveRange(blockToFormat: LeaveBlock): string {
  const start = new Date(blockToFormat.startDate);
  const end = new Date(blockToFormat.endDate);
  if (isSameDay(start, end)) return format(start, 'd MMM yyyy');
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }
  return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
}

export interface NewArtistInput {
  firstName: string;
  lastName: string;
  studioId: StudioId;
}

export function createArtist(input: NewArtistInput, id: string, joinedAtIso: string): Artist {
  const displayName = `${input.firstName} ${input.lastName}`.trim();
  return {
    id,
    firstName: input.firstName,
    lastName: input.lastName,
    displayName,
    studioId: input.studioId,
    instagramHandle: '',
    styleSpeciality: [],
    bio: '',
    isApprentice: false,
    commissionRate: 0.5,
    availability: emptyAvailability(),
    joinedAt: joinedAtIso,
    rates: { ...DEFAULT_RATES, ...defaultArtistDeposits(DEFAULT_RATES) },
    archived: false,
  };
}

export type DayOffKind = 'rdo' | 'comp' | 'leave';

export interface DayOffInfo {
  kind: DayOffKind;
  note?: string;
  source: 'block' | 'pattern';
}

export interface DayOffInput {
  kind: DayOffKind;
  date: string;             // yyyy-MM-dd (single day)
  note?: string;
  compensatesDate?: string; // for kind 'comp'
  confirmedBy?: string;
}

// getDay() index (0=Sun..6=Sat) → DayOfWeek key.
const DOW_BY_INDEX: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export function leaveBlockKind(type: LeaveBlock['type']): DayOffKind {
  if (type === 'rdo') return 'rdo';
  if (type === 'comp') return 'comp';
  return 'leave'; // flex-swap | time-off
}

/** A legacy RDO entry's studio day: a bare `yyyy-MM-dd` as is, an instant by its studio day. */
function rdoDayKey(d: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : studioDayKey(d);
}

/**
 * Single source of truth for "is this artist off on this date, and why".
 * Order: explicit leave block → legacy rdoDates → weekly pattern → working (null).
 */
export function resolveDayOff(av: ArtistAvailability, day: Date): DayOffInfo | null {
  const key = format(day, 'yyyy-MM-dd');
  const covering = av.leaveBlocks.find((b) => {
    const s = studioDayKey(b.startDate);
    const e = studioDayKey(b.endDate);
    return key >= s && key <= e;
  });
  if (covering) return { kind: leaveBlockKind(covering.type), note: covering.note, source: 'block' };
  // An RDO date may carry a time (a seeded `+10:00` instant): compare studio days (8uf3).
  if (av.rdoDates.some((d) => rdoDayKey(d) === key)) return { kind: 'rdo', source: 'block' };
  const dow = DOW_BY_INDEX[day.getDay()]!;
  if (!av.pattern[dow]) return { kind: 'rdo', source: 'pattern' };
  return null;
}

export function canScheduleBookingOn(av: ArtistAvailability, day: Date): boolean {
  return resolveDayOff(av, day) === null;
}

export function buildDayOffBlock(input: DayOffInput, id: string): LeaveBlock {
  if (input.kind === 'comp' && !input.note) {
    throw new Error('A comp day off requires a note.');
  }
  const iso = studioDayStartIso(input.date);
  const type: LeaveBlock['type'] =
    input.kind === 'rdo' ? 'rdo' : input.kind === 'comp' ? 'comp' : 'time-off';
  return {
    id,
    startDate: iso,
    endDate: iso,
    type,
    ...(input.note ? { note: input.note } : {}),
    ...(input.kind === 'comp' && input.compensatesDate ? { compensatesDate: input.compensatesDate } : {}),
    ...(input.confirmedBy ? { confirmedBy: input.confirmedBy } : {}),
  };
}
